import React, { useState, useEffect } from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';import {useTranslation} from 'react-i18next';import {Button} from '@/components/ui/button';import {BookOpen, Video, MessageSquare, ArrowRight, Sun, Moon, Sparkles, Loader2, Crown, BookOpenCheck, Microscope, Users, Star, Pin, ShieldCheck, ArrowUpRight, ChevronDown, Cross, Languages, Landmark} from 'lucide-react';import LanguageSelector from '@/components/LanguageSelector';
import {useTheme} from '@/contexts/ThemeContext';import axios from 'axios';
import {toast} from 'sonner';import {API_URL as API} from '@/utils/apiConfig';
import SmartInstallBanner from '@/components/SmartInstallBanner';
import AppStoreBadges from '@/components/AppStoreBadges';
export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { colorMode, changeColorMode } = useTheme();
  const [guestLoading, setGuestLoading] = useState(false);
  const [trialEnabled, setTrialEnabled] = useState(false);
  const [featuredReviews, setFeaturedReviews] = useState([]);
  const [sourcesTotal, setSourcesTotal] = useState(null);
  const [heroIdx, setHeroIdx] = useState(0);

  // Hero mockup question/verse pairs — rotate every ~7s so returning
  // visitors see a fresh piece of proof each visit. All verses are
  // real Scripture quoted verbatim; the chip labels the translation
  // so it visibly reinforces the "we never invent verses" promise.
  // Questions kept ≤30 chars so they fit the typing line on one row.
  const heroPairs = React.useMemo(() => ([
    {
      q: t('generated.landingpage_why_does_god_f273'),
      intro: t('generated.landingpage_paul_answers_in_fc27'),
      quote: t('generated.landingpage_suffering_produces_endurance_6104'),
      ref: t('generated.landingpage_romans_5_3_f69b'),
    },
    {
      q: t('generated.landingpage_what_about_anxiety_6a4e'),
      intro: t('generated.landingpage_paul_writes_to_0f64'),
      quote: t('generated.landingpage_do_not_be_1408'),
      ref: t('generated.landingpage_philippians_4_6_882e'),
    },
    {
      q: t('generated.landingpage_how_do_i_0b65'),
      intro: t('generated.landingpage_paul_roots_forgiveness_406b'),
      quote: t('generated.landingpage_forgive_one_another_665f'),
      ref: t('generated.landingpage_ephesians_4_32_078b'),
    },
    {
      q: t('generated.landingpage_is_doubt_a_7e66'),
      intro: t('generated.landingpage_jude_offers_this_56d6'),
      quote: t('generated.landingpage_be_merciful_to_a128'),
      ref: t('generated.landingpage_jude_1_22_6d66'),
    },
    {
      // YouTube sermon analysis — variant kind with a URL-style question
      // bubble and a "Sermon" chip in place of the verse chip. Same
      // animation timeline, different visual proof: shows visitors that
      // Eili can also unpack sermons and videos, not just verse Q&A.
      kind: 'youtube',
      q: t('generated.landingpage_youtu_be_ephgrace_9fcb'),
      intro: t('generated.landingpage_analyzing_this_42_0f5f'),
      quote: t('generated.landingpage_the_pastor_frames_6e06'),
      ref: t('generated.landingpage_sermon_ephesians_2_1961'),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [i18n.language]);

  useEffect(() => {
    // Advance the hero pair every 7 seconds. Slightly longer than the
    // 6s animation so the verse chip has a beat to breathe before the
    // next question types in.
    const id = setInterval(() => {
      setHeroIdx((i) => (i + 1) % heroPairs.length);
    }, 7000);
    return () => clearInterval(id);
  }, [heroPairs.length]);

  const currentPair = heroPairs[heroIdx];

  // Capture affiliate referral code from URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      localStorage.setItem('eili_ref_code', ref);
    }
  }, [searchParams]);

  useEffect(() => {
    axios.get(`${API}/feature-flags/landing-trial`).then(res => setTrialEnabled(res.data.enabled)).catch(() => {});
    axios.get(`${API}/reviews/featured`).then(res => setFeaturedReviews(res.data.reviews || [])).catch(() => {});
    axios.get(`${API}/historical-sources`).then(res => setSourcesTotal(res.data.total_entries || null)).catch(() => {});
  }, []);

  // Redirect to /app if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        // User is authenticated, redirect to app
        window.location.replace('/app');
      } catch {
        // Not authenticated, stay on landing page
      }
    };
    checkAuth();
  }, []);

  // Reveal sticky mobile CTA after the user scrolls ~40% into the page.
  // Toggles a body class so the pill can slide up from the bottom via CSS.
  useEffect(() => {
    const onScroll = () => {
      const threshold = Math.min(window.innerHeight * 0.6, 500);
      if (window.scrollY > threshold) {
        document.body.classList.add('show-landing-cta');
      } else {
        document.body.classList.remove('show-landing-cta');
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.body.classList.remove('show-landing-cta');
    };
  }, []);

  const handleGuestStart = async () => {
    setGuestLoading(true);
    try {
      await axios.post(`${API}/auth/guest`, {}, { withCredentials: true });
      // Use replace to prevent back button issues
      window.location.replace('/app');
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Something went wrong';
      toast.error(typeof msg === 'string' ? msg : 'Failed to start session');
    } finally {
      setGuestLoading(false);
    }
  };

  return (
    <main className="page-container relative overflow-x-hidden" style={{ background: 'var(--theme-background)' }} role="main">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-20 pointer-events-none" style={{ background: 'radial-gradient(ellipse, var(--theme-accent) 0%, transparent 70%)' }} />
      <div className="relative max-w-5xl mx-auto px-4 py-8 page-content w-full" style={{ paddingTop: '2rem' }}>
        {/* Top navigation — audit D2.4. Gives visitors a way to reach
            pricing, FAQ, and other surfaces before scrolling; keeps the
            "Explore free" CTA persistent so a decision is never more than
            one click away. */}
        <nav className="flex items-center justify-between gap-2 mb-8" aria-label={t('generated.landingpage_primary_f932')} data-testid="landing-top-nav">
          <a href="/" className="flex items-center gap-1.5 shrink-0" aria-label={t('generated.landingpage_eili_home_8f39')}>
            <img src="/logo.png" alt={t('generated.landingpage_eili_bible_study_e6ff')} className="h-7 w-7 sm:h-8 sm:w-8" />
            <span className="text-base sm:text-lg font-semibold" style={{ color: 'var(--theme-text)', fontFamily: "'Playwrite ES Deco', cursive" }}>{t('generated.landingpage_eili_6028')}</span>
          </a>
          <div className="flex items-center gap-0.5 sm:gap-2 flex-nowrap min-w-0">
            <button onClick={() => navigate('/pricing')} className="px-2 sm:px-3 py-2 rounded-full text-sm font-medium hover:opacity-80 transition whitespace-nowrap" style={{ color: 'var(--theme-text)' }} data-testid="nav-pricing">{t('generated.landingpage_pricing_7e43')}</button>
            <a href="#features" className="px-3 py-2 rounded-full text-sm font-medium hover:opacity-80 transition hidden md:inline-block" style={{ color: 'var(--theme-text)' }} data-testid="nav-features">{t('generated.landingpage_features_8df7')}</a>
            <a href="#reviews" className="px-3 py-2 rounded-full text-sm font-medium hover:opacity-80 transition hidden md:inline-block" style={{ color: 'var(--theme-text)' }} data-testid="nav-reviews">{t('generated.landingpage_reviews_658d')}</a>
            <button onClick={() => navigate('/faq')} className="px-2 sm:px-3 py-2 rounded-full text-sm font-medium hover:opacity-80 transition whitespace-nowrap" style={{ color: 'var(--theme-text)' }} data-testid="nav-faq">{t('generated.landingpage_faq_0554')}</button>
            <button
              onClick={() => changeColorMode(colorMode === 'dark' ? 'light' : 'dark')}
              className="p-2 rounded-full transition-all shrink-0"
              style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
              data-testid="landing-mode-toggle"
              aria-label={t('generated.landingpage_toggle_color_mode_9ceb')}
            >
              {colorMode === 'dark'
                ? <Sun className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
                : <Moon className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />}
            </button>
            <div className="rounded-full px-1 hidden sm:block" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
              <LanguageSelector />
            </div>
            <Button
              onClick={handleGuestStart}
              disabled={guestLoading}
              className="hidden sm:inline-flex ml-1 px-4 py-2 rounded-full text-sm font-semibold text-white shadow-md"
              style={{ background: `linear-gradient(135deg, var(--theme-accent) 0%, var(--theme-accentLight) 100%)` }}
              data-testid="nav-cta"
            >
              {t('generated.landingpage_explore_free_8409')}
            </Button>
          </div>
        </nav>

        {/* Smart install banner — appears only for mobile-web visitors
            on iOS / Android who are NOT already in the Despia native
            app. Respects a 7-day dismissal cooldown + 3-per-session cap
            so it never nags. */}
        <SmartInstallBanner source="landing-banner" className="mb-6" />

        {/* Hero Section — desktop uses 2-column (copy + phone mockup),
            mobile stacks with mockup below the CTAs. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-10">
          {/* LEFT: copy + CTAs (kept left-aligned on desktop, centered on mobile) */}
          <div className="text-center lg:text-left">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3 leading-tight" style={{ color: 'var(--theme-text)' }} data-testid="landing-h1">
            {t('generated.landingpage_your_bible_study_eb5f')}
          </h1>
          <p className="text-xl sm:text-2xl font-semibold max-w-2xl mx-auto lg:mx-0 leading-snug mb-3" style={{ color: 'var(--theme-text)' }} data-testid="landing-headline">
            {t('generated.landingpage_there_s_more_7c94')}
          </p>
          <p className="text-base sm:text-lg max-w-2xl mx-auto lg:mx-0 leading-relaxed mb-3" style={{ color: 'var(--theme-textSecondary)' }} data-testid="landing-subhead">
            {t('generated.landingpage_a_bible_study_7dee')}
          </p>

          {/* Counter-positioning chip — surfaces the $400+ Logos price
              context above the fold so cost-conscious visitors immediately
              recognize the value gap (audit D1.2, FIT). `rounded-2xl` keeps
              the shape clean when the text wraps to two lines on mobile. */}
          <div className="flex justify-center lg:justify-start mb-6">
            <p className="inline-flex items-start gap-2 text-[13px] font-medium rounded-2xl px-3.5 py-2 max-w-fit" style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)', color: 'var(--theme-text)' }} data-testid="landing-price-anchor">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#10b981' }} />
              <span>{t('generated.landingpage_scholar_grade_depth_1319')}</span>
            </p>
          </div>

          {/* Three supporting benefit chips — carry the promise without
              weighing the subhead down. Anti-hallucination promise
              lives here as a proof point, not a lead. */}
          <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-7 max-w-2xl mx-auto lg:mx-0" data-testid="landing-benefit-chips">
            <li className="flex items-start gap-2 rounded-xl px-3 py-2 text-[13px] leading-snug" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}>
              <MessageSquare className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--theme-accent)' }} />
              <span><strong>{t('generated.landingpage_every_question_welcome_e6f4')}</strong> {t('generated.landingpage_from_who_wrote_d28d')}</span>
            </li>
            <li className="flex items-start gap-2 rounded-xl px-3 py-2 text-[13px] leading-snug" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}>
              <BookOpen className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--theme-accent)' }} />
              <span><strong>{t('generated.landingpage_context_on_every_b5ea')}</strong> {t('generated.landingpage_historical_background_cross_9f3e')}</span>
            </li>
            <li className="flex items-start gap-2 rounded-xl px-3 py-2 text-[13px] leading-snug" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}>
              <Users className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--theme-accent)' }} />
              <span><strong>{t('generated.landingpage_meets_you_where_c551')}</strong> {t('generated.landingpage_first_time_reader_2367')}</span>
            </li>
          </ul>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-3 mb-3">
            <Button
              onClick={handleGuestStart}
              disabled={guestLoading}
              className="w-full sm:w-auto px-10 py-6 rounded-xl text-base font-medium flex items-center justify-center gap-2 text-white transition-all hover:opacity-90 shadow-xl"
              style={{ background: `linear-gradient(135deg, var(--theme-accent) 0%, var(--theme-accentLight) 100%)` }}
              data-testid="try-free-button"
            >
              {guestLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Sparkles className="h-5 w-5" />
              )}
              {t('generated.landingpage_explore_free_no_6a61')}
            </Button>
            <Button
              onClick={() => navigate('/login')}
              variant="ghost"
              className="w-full sm:w-auto px-10 py-6 rounded-xl text-base font-medium flex items-center justify-center gap-2 transition-all"
              style={{ color: 'var(--theme-text)', border: '1px solid var(--glass-border)' }}
              data-testid="sign-in-button"
            >
              {t('login.signin', 'Sign In')}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs max-w-md mx-auto lg:mx-0 mb-2 flex items-center gap-1.5 justify-center lg:justify-start" style={{ color: 'var(--theme-text)' }} data-testid="landing-cta-trust">
            <ShieldCheck className="h-3.5 w-3.5" style={{ color: '#10b981' }} />
            <span><strong>{t('generated.landingpage_no_credit_card_7e19')}</strong> {t('generated.landingpage_cancel_anytime_4c51')}</span>
          </p>
          <p className="text-xs max-w-md mx-auto lg:mx-0 mb-6 leading-relaxed" style={{ color: 'var(--theme-textSecondary)', opacity: 0.7 }} data-testid="landing-pricing-note">
            {t('generated.landingpage_free_forever_for_2b72')}
          </p>

          {/* App-store badges — auto-hides inside Despia native */}
          <AppStoreBadges source="landing-hero" className="mb-6" />
          </div>

          {/* RIGHT: animated phone mockup — CSS-only, no external asset.
              Loops a chat: user asks a hard question, Eili replies with a
              verse chip. This IS the "video hero" without needing an
              actual video file to ship. */}
          <div className="flex justify-center lg:justify-end" data-testid="landing-phone-mockup" role="img" aria-label={t('generated.landingpage_eili_bible_study_84a9')}>
            <div
              className="relative w-[280px] h-[560px] rounded-[42px] p-3 shadow-2xl"
              style={{
                background: 'linear-gradient(160deg, #1a1a1e 0%, #0a0a0d 100%)',
                border: '2px solid #2a2a30',
              }}
              aria-hidden="true"
            >
              {/* notch */}
              <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-6 rounded-full" style={{ background: '#0a0a0d' }} />
              {/* screen */}
              <div
                className="w-full h-full rounded-[32px] overflow-hidden relative flex flex-col"
                style={{ background: 'var(--theme-bg, #0f172a)' }}
              >
                {/* status bar */}
                <div className="h-8 flex items-center justify-between px-5 text-[10px] font-semibold" style={{ color: 'var(--theme-text)' }}>
                  <span>9:41</span>
                  <span>◉ ◉ ◉</span>
                </div>
                {/* header */}
                <div className="px-4 pt-1 pb-3 border-b" style={{ borderColor: 'var(--glass-border)' }}>
                  <p className="text-[11px] font-semibold" style={{ color: 'var(--theme-text)' }}>{t('generated.landingpage_eili_6028')}</p>
                  <p className="text-[9px]" style={{ color: 'var(--theme-textSecondary)' }}>
                    {currentPair.kind === 'youtube'
                      ? t('generated.landingpage_sermon_analysis_ready')
                      : `${(currentPair.ref.match(/\(([^)]+)\)/) || [null, 'ESV'])[1]} · ${t('generated.landingpage_online_status')}`}
                  </p>
                </div>
                {/* chat body */}
                <div key={heroIdx} className="flex-1 px-3 py-4 space-y-3 overflow-hidden" style={{ background: 'var(--theme-bg, #0f172a)' }}>
                  {/* user message — types in */}
                  <div className="flex justify-end">
                    <div
                      className="max-w-[80%] px-3 py-2 rounded-2xl rounded-tr-sm text-[11px] leading-snug landing-mockup-user"
                      style={{ background: 'var(--theme-accent)', color: 'white' }}
                    >
                      <span className="landing-typing">{currentPair.q}</span>
                    </div>
                  </div>
                  {/* eili reply — fades in after typing */}
                  <div className="flex justify-start landing-mockup-reply">
                    <div
                      className="max-w-[85%] px-3 py-2 rounded-2xl rounded-tl-sm text-[11px] leading-snug"
                      style={{ background: 'var(--glass-bg)', color: 'var(--theme-text)', border: '1px solid var(--glass-border)' }}
                    >
                      {currentPair.intro}
                      <div
                        className="mt-2 px-2 py-1.5 rounded-lg text-[10px] italic"
                        style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.25)', color: 'var(--theme-text)' }}
                      >
                        {currentPair.quote}
                      </div>
                      <span
                        className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-[9px] font-semibold landing-mockup-chip"
                        style={{ background: currentPair.kind === 'youtube' ? 'rgba(239,68,68,0.15)' : 'rgba(99,102,241,0.15)', color: currentPair.kind === 'youtube' ? '#ef4444' : 'var(--theme-accent)', border: `1px solid ${currentPair.kind === 'youtube' ? 'rgba(239,68,68,0.3)' : 'rgba(99,102,241,0.3)'}` }}
                      >
                        {currentPair.kind === 'youtube' ? '🎥' : '📖'} {currentPair.ref}
                      </span>

                      {/* Collapsed analysis dropdowns — signal that Eili
                          offers deeper study in every answer (Christ-
                          centered reading, original languages, cultural
                          context). Not actually expandable; visual proof
                          that these tools exist inside the app. */}
                      <div className="mt-2.5 space-y-1 landing-mockup-dropdowns">
                        <MockupDropdown icon={Cross} label={t('generated.landingpage_how_this_points_d3d0')} />
                        <MockupDropdown icon={Languages} label={t('generated.landingpage_original_language_greek_5671')} />
                        <MockupDropdown icon={Landmark} label={t('generated.landingpage_historical_cultural_context_d3fc')} />
                      </div>
                    </div>
                  </div>
                </div>
                {/* input bar */}
                <div className="px-3 py-2 border-t" style={{ borderColor: 'var(--glass-border)' }}>
                  <div className="rounded-full px-3 py-1.5 text-[10px]" style={{ background: 'var(--glass-bg)', color: 'var(--theme-textSecondary)' }}>
                    {t('generated.landingpage_ask_anything_ed35')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trial Offer + install banner block — kept below the hero grid */}
        <div className="text-center mb-10">
          {/* Trial Offer — admin-controlled */}
          {trialEnabled && (
            <div
              className="inline-flex items-center gap-3 rounded-2xl px-6 py-4 cursor-pointer transition-all hover:scale-[1.02] active:scale-95"
              style={{ background: 'linear-gradient(135deg, rgba(234,179,8,0.12) 0%, rgba(234,179,8,0.04) 100%)', border: '1px solid rgba(234,179,8,0.25)', backdropFilter: 'blur(12px)' }}
              onClick={() => navigate('/signup?trial=true&src=banner')}
              data-testid="trial-offer-banner"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/signup?trial=true&src=banner')}
            >
              <div className="p-2 rounded-xl" style={{ background: 'rgba(234,179,8,0.15)' }}>
                <Crown className="h-5 w-5" style={{ color: '#eab308' }} />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                  {t('app.trial_title', 'Try Premium — 7 Days Free')}
                </p>
                <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
                  {t('app.trial_desc', 'Full access to Commentary, Greek Analysis & more')}
                </p>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0" style={{ color: '#eab308' }} />
            </div>
          )}
          {trialEnabled && (
            <p className="mt-2 text-xs" style={{ color: 'var(--theme-textSecondary)' }} data-testid="trial-offer-terms">
              {t('generated.landingpage_7_days_free_92a4')}
            </p>
          )}
        </div>

        {/* Feature Cards */}
        <div id="features" className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-3xl mx-auto scroll-mt-20">
          <FeatureCard icon={MessageSquare} title={t('app.features.chat.title')} desc={t('app.features.chat.desc')} testId="feature-card-chat" />
          <FeatureCard icon={BookOpen} title={t('app.features.commentary.title')} desc={t('app.features.commentary.desc')} testId="feature-card-commentary" />
          <FeatureCard icon={BookOpenCheck} title={t('app.features.bible.title')} desc={t('app.features.bible.desc')} testId="feature-card-bible" />
          <FeatureCard icon={Video} title={t('app.features.video.title')} desc={t('app.features.video.desc')} testId="feature-card-video" />
          <FeatureCard icon={Microscope} title={t('app.features.deep_study.title')} desc={t('app.features.deep_study.desc')} testId="feature-card-deep-study" />
          <FeatureCard icon={Users} title={t('app.features.share.title')} desc={t('app.features.share.desc')} testId="feature-card-share" />
        </div>

        {/* Built for — persona cards. Speaks directly to the different
            kinds of people who arrive at Eili so no one bounces because
            they think it "isn't for them". */}
        <div className="mt-14 max-w-4xl mx-auto" data-testid="landing-personas-section">
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: 'var(--theme-text)' }}>
              {t('generated.landingpage_built_for_anyone_0797')}
            </h2>
            <p className="text-sm max-w-xl mx-auto" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.landingpage_pick_the_person_c25e')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <PersonaCard title={t('generated.landingpage_the_questioner_0299')} body="Bring the hard things — suffering, doubt, contradictions. Get answers grounded in the Bible, never in opinion." emoji="🤔" testId="persona-questioner" />
            <PersonaCard title={t('generated.landingpage_the_daily_reader_109a')} body="Turn a quiet five minutes into deeper understanding. Every verse Eili shares is real, from your chosen translation." emoji="☕" testId="persona-daily" />
            <PersonaCard title={t('generated.landingpage_the_teacher_pastor_3862')} body="Prep lessons faster with verified cross-references, Greek &amp; Hebrew word roots, and quotes from trusted commentators." emoji="🎙️" testId="persona-teacher" />
            <PersonaCard title={t('generated.landingpage_the_seeker_b258')} body="New to the Bible? Start here. No jargon, no gatekeeping — just Scripture explained plainly, at your pace." emoji="🌱" testId="persona-seeker" />
            <PersonaCard title={t('generated.landingpage_the_scholar_6f03')} body="Cross-reference 12+ translations (KJV, ESV, NASB, LSB, AMP, LBLA) alongside Josephus, ISBE, and the Church Fathers." emoji="📚" testId="persona-scholar" />
            <PersonaCard title={t('generated.landingpage_the_small_group_f1ac')} body="Share reflections, ask questions together, and study the same passage across devices — all in one place." emoji="👥" testId="persona-group" />
          </div>
        </div>

        {/* Why Eili — quiet comparison strip. No calling anything else
            out by name; we lead with what makes Eili different. */}
        <div className="mt-14 max-w-4xl mx-auto" data-testid="landing-why-section">
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: 'var(--theme-text)' }}>
              {t('generated.landingpage_why_people_stay_41b8')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <WhyCard title={t('generated.landingpage_depth_not_devotional_d4f4')} body="Move past 5-minute reflections. Pull up commentary, original-language roots, and cross-references the moment a passage catches your attention — no seminary required." testId="why-depth" />
            <WhyCard title={t('generated.landingpage_answers_that_hold_0aa2')} body="Every verse Eili quotes comes straight from your chosen translation. Every claim shows its work. Study, teach, and share with confidence." testId="why-real-scripture" />
            <WhyCard title={t('generated.landingpage_scholar_grade_phone_f7ab')} body="Cross-references, Greek and Hebrew analysis, vetted commentators — without the desktop-only interface or the $400+ price tag." testId="why-scholar-simple" />
          </div>
        </div>

        {/* Trust section — "Backed by verified sources" */}
        <div
          className="mt-14 max-w-3xl mx-auto rounded-3xl p-6 sm:p-8 text-center"
          style={{
            background: 'linear-gradient(135deg, rgba(16,185,129,0.10), rgba(99,102,241,0.08))',
            border: '1px solid rgba(16,185,129,0.25)',
            backdropFilter: 'blur(12px)',
          }}
          data-testid="landing-sources-section"
        >
          <div
            className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center"
            style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)' }}
          >
            <ShieldCheck className="h-6 w-6" style={{ color: '#10b981' }} />
          </div>
          <h2 className="text-lg sm:text-xl font-semibold mb-2" style={{ color: 'var(--theme-text)' }}>
            {sourcesTotal ? `Backed by ${sourcesTotal}+ verified scholarly entries` : 'Backed by verified scholarly sources'}
          </h2>
          <p className="text-sm max-w-xl mx-auto mb-4 leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }}>
            {t('generated.landingpage_eili_s_answers_b0bb')} <strong>{t('generated.landingpage_the_actual_bible_bc06')}</strong> {t('generated.landingpage_and_a_curated_6264')}
          </p>
          <button
            onClick={() => navigate('/sources')}
            className="inline-flex items-center gap-1.5 text-sm font-medium underline hover:opacity-80 transition-opacity"
            style={{ color: '#10b981' }}
            data-testid="landing-view-sources-link"
          >
            {t('generated.landingpage_view_our_full_2200')}
            <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>

        {/* Community feature strip — audit FIT. Surfaces the social layer
            the Terms describe (posting, reacting, friends, shared plans)
            so visitors evaluating group study see it before they leave. */}
        <div className="mt-14 max-w-3xl mx-auto rounded-3xl p-6 sm:p-8" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }} data-testid="landing-community-strip">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl shrink-0 flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)' }}>
              <Users className="h-6 w-6" style={{ color: 'var(--theme-accent)' }} />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold mb-1" style={{ color: 'var(--theme-text)' }}>{t('generated.landingpage_study_alongside_friends_f2da')}</h2>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('generated.landingpage_share_insights_react_5ffd')}
              </p>
            </div>
          </div>
        </div>

        {/* Homepage FAQ preview — audit D1.4. Answers the top three
            objections (accuracy, denominational stance, cost) before the
            visitor bounces. Full FAQ page (with structured data) lives at
            /faq for depth. */}
        <div className="mt-14 max-w-3xl mx-auto" data-testid="landing-faq-preview">
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: 'var(--theme-text)' }}>{t('generated.landingpage_common_questions_f441')}</h2>
            <p className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>{t('generated.landingpage_what_people_ask_d2e1')}</p>
          </div>
          <div className="space-y-3">
            {[
              { q: t('generated.landingpage_how_does_eili_f3ba'), a: t('generated.landingpage_every_verse_is_49fe') },
              { q: t('generated.landingpage_does_eili_push_23b5'), a: t('generated.landingpage_no_when_a_b75a') },
              { q: t('generated.landingpage_what_s_the_b561'), a: t('generated.landingpage_free_gives_you_7028') },
            ].map((item, i) => (
              <details key={i} className="rounded-xl px-4 py-3 group" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }} data-testid={`landing-faq-preview-item-${i}`}>
                <summary className="cursor-pointer text-sm font-semibold flex items-center justify-between" style={{ color: 'var(--theme-text)' }}>
                  {item.q}
                  <ChevronDown className="h-4 w-4 opacity-60 transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }}>{item.a}</p>
              </details>
            ))}
          </div>
          <div className="text-center mt-5">
            <button onClick={() => navigate('/faq')} className="inline-flex items-center gap-1.5 text-sm font-medium underline hover:opacity-80" style={{ color: 'var(--theme-accent)' }} data-testid="landing-faq-preview-cta">
              {t('generated.landingpage_read_the_full_08b6')}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Testimonials Section */}
        {featuredReviews.length > 0 && (
          <div id="reviews" className="mt-14 max-w-3xl mx-auto scroll-mt-20" data-testid="landing-testimonials-section">
            <div className="text-center mb-6">
              <h2 className="text-base sm:text-lg font-semibold mb-1" style={{ color: 'var(--theme-text)' }}>
                {t('generated.landingpage_what_people_are_7f51')}
              </h2>
              <p className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('generated.landingpage_hear_from_our_b9e5')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {featuredReviews.slice(0, 4).map(review => (
                <div
                  key={review.review_id}
                  className="rounded-2xl p-5 transition-all hover:scale-[1.01]"
                  style={{ background: 'var(--glass-bg)', border: review.pinned ? '1px solid var(--theme-accent, #6366f1)30' : '1px solid var(--glass-border)', backdropFilter: 'blur(12px)' }}
                  data-testid={`landing-review-${review.review_id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-accent)' }}
                    >
                      {(review.display_name || 'A')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium truncate" style={{ color: 'var(--theme-text)' }}>{review.display_name}</p>
                        {review.pinned && <Pin className="h-3 w-3 shrink-0" style={{ color: 'var(--theme-accent)' }} />}
                      </div>
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map(i => (
                          <Star key={i} className="h-3 w-3" fill={i <= review.rating ? '#f59e0b' : 'transparent'} stroke={i <= review.rating ? '#f59e0b' : 'var(--theme-textSecondary)'} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }}>
                    {review.comment.length > 150 ? review.comment.slice(0, 150) + '...' : review.comment}
                  </p>
                  {review.admin_reply && (
                    <div className="mt-2 rounded-xl p-2.5" style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)' }}>
                      <span className="text-[11px] font-semibold block mb-0.5" style={{ color: 'var(--theme-accent)' }}>{t('generated.landingpage_eili_team_0a15')}</span>
                      <p className="text-xs leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }}>
                        {review.admin_reply.length > 100 ? review.admin_reply.slice(0, 100) + '...' : review.admin_reply}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="text-center mt-5">
              <Button
                onClick={() => navigate('/reviews')}
                variant="ghost"
                className="rounded-xl gap-2 text-sm"
                style={{ color: 'var(--theme-accent)', border: '1px solid var(--glass-border)' }}
                data-testid="see-all-reviews-btn"
              >
                {t('generated.landingpage_see_all_reviews_c10b')} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Sticky mobile CTA — appears after scrolling past the hero on
          phones/tablets. Hidden on desktop (>=lg) via CSS. Reuses the
          guest-start flow so it's a one-tap "explore free". */}
      <div className="landing-sticky-cta" data-testid="landing-sticky-cta">
        <Button
          onClick={handleGuestStart}
          disabled={guestLoading}
          className="rounded-full px-6 py-5 text-sm font-semibold flex items-center gap-2 text-white shadow-2xl"
          style={{ background: `linear-gradient(135deg, var(--theme-accent) 0%, var(--theme-accentLight) 100%)` }}
          data-testid="landing-sticky-cta-btn"
        >
          {guestLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          {t('generated.landingpage_explore_free_no_6a61')}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </main>
  );
}

// Collapsed accordion row inside the hero mockup. Purely visual — signals
// that Eili's answers ship with expandable deep-study sections (Christ-
// centered reading, original language, cultural context). No expand
// behavior on purpose; keeps the mockup compact and non-distracting.
function MockupDropdown({ icon: Icon, label }) {
  return (
    <div
      className="flex items-center justify-between rounded-md px-2 py-1"
      style={{ background: 'var(--glass-bg-active, rgba(255,255,255,0.04))', border: '1px solid var(--glass-border)' }}
      aria-hidden="true"
    >
      <span className="inline-flex items-center gap-1.5 text-[9px] font-medium" style={{ color: 'var(--theme-text)' }}>
        <Icon className="h-2.5 w-2.5" style={{ color: 'var(--theme-accent)' }} />
        {label}
      </span>
      <ChevronDown className="h-2.5 w-2.5 opacity-60" style={{ color: 'var(--theme-textSecondary)' }} />
    </div>
  );
}



function FeatureCard({ icon: Icon, title, desc, testId }) {
  return (
    <div
      className="rounded-2xl p-6 transition-all hover:scale-[1.02]"
      style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(12px)' }}
      data-testid={testId}
    >
      <div className="p-3 rounded-xl inline-flex mb-3" style={{ background: 'var(--glass-bg-active)' }}>
        <Icon className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
      </div>
      <h3 className="text-base font-semibold mb-1.5" style={{ color: 'var(--theme-text)' }}>{title}</h3>
      <p className="text-sm leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }}>{desc}</p>
    </div>
  );
}

function PersonaCard({ title, body, emoji, testId }) {
  return (
    <div
      className="rounded-2xl p-5 transition-all hover:scale-[1.02] text-left"
      style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(12px)' }}
      data-testid={testId}
    >
      <div className="text-3xl mb-2 leading-none" aria-hidden="true">{emoji}</div>
      <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--theme-text)' }}>{title}</h3>
      <p className="text-sm leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }} dangerouslySetInnerHTML={{ __html: body }} />
    </div>
  );
}

function WhyCard({ title, body, testId }) {
  return (
    <div
      className="rounded-2xl p-5 text-left"
      style={{
        background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(16,185,129,0.05))',
        border: '1px solid var(--glass-border)',
        backdropFilter: 'blur(12px)',
      }}
      data-testid={testId}
    >
      <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--theme-text)' }}>{title}</h3>
      <p className="text-sm leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }}>{body}</p>
    </div>
  );
}
